import React from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import './GuestWelcomeModal.css';

// ── THE TRIAL'S ORIENTATION ──────────────────────────────────────────────────
// Shown the moment a guest lands on "/try": a short guide to how the trial
// works, and nothing else.
//
// IT USED TO ASK FOR A LANGUAGE FIRST, on the reasoning that a guest has no
// account whose settings could have set one. That stopped being true: the
// language is now asked once on the landing page, the first time anyone opens
// the site, and remembered on the device (see LanguageModal). Anyone reaching
// /try has already been through that — so the step was asking a question that
// had been answered, and putting it in front of the guide every single time.
export default function GuestWelcomeModal({ onClose }) {
  const { t } = useT();

  const steps = [
    {
      title: t('guest_welcome_step1_title'),
      body: t('guest_welcome_step1_body'),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M3 3h18v18H3V3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
          <path d="M3 9h18M3 15h18M9 3v18M15 3v18" stroke="currentColor" strokeWidth="1.2" opacity="0.55"/>
        </svg>
      ),
    },
    {
      title: t('guest_welcome_step2_title'),
      // The Floor Plan trigger button moves from the sidebar (bottom-left)
      // on desktop to a floating pill (top-right) on mobile/tablet — swap
      // which sentence is visible via CSS instead of picking one at render
      // time, so it stays correct across a live viewport resize too.
      body: (
        <>
          <span className="gwm-desktop-only">{t('guest_welcome_step2_body')}</span>
          <span className="gwm-mobile-only">{t('guest_welcome_step2_body_mobile')}</span>
        </>
      ),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
          <path d="M3 12h9M12 4v16" stroke="currentColor" strokeWidth="1.4"/>
        </svg>
      ),
    },
    {
      title: t('guest_welcome_step3_title'),
      body: t('guest_welcome_step3_body'),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
          <path d="M12 12l8-4.5M12 12v9M12 12L4 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  return createPortal(
    <div className="gwm-overlay" onClick={onClose}>
      <div className="gwm-box" onClick={e => e.stopPropagation()}>
        <button className="gwm-close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="gwm-brand">
          <img src="/images/Tunify Original logo tab.png" alt="Tunify" className="gwm-brand-logo" />
        </div>

        <h2 className="gwm-title">{t('guest_welcome_title')}</h2>
        <p className="gwm-sub">{t('guest_welcome_sub')}</p>

        <ol className="gwm-steps">
          {steps.map((s, i) => (
            <li className="gwm-step" key={i}>
              <span className="gwm-step-num">{i + 1}</span>
              <span className="gwm-step-icon">{s.icon}</span>
              <span className="gwm-step-text">
                <strong>{s.title}</strong>
                <span>{s.body}</span>
              </span>
            </li>
          ))}
        </ol>

        <button className="gwm-cta" onClick={onClose}>{t('guest_welcome_cta')}</button>
      </div>
    </div>,
    document.body
  );
}
